import { useEffect } from "react";
import { useSiteNavigate } from "./SiteLink";
import { useRelativePath } from "./TemplateContext";
import { useContentEditingSafe } from "./ContentEditingContext";
import { seoPages, seoKey, slugToPageId } from "../../data/seoData";

/**
 * SeoHead — applies document.title and meta description
 * based on the current page and any SEO overrides from the editing system.
 *
 * Also handles slug redirects: when a page's slug has been changed,
 * visiting the old slug will redirect to the new one (except in edit mode).
 *
 * Renders nothing to the DOM.
 */
export function SeoHead() {
  const navigate = useSiteNavigate();
  const relativePath = useRelativePath();
  const ctx = useContentEditingSafe();

  const getContent = ctx?.getContent ?? ((key: string, def: string) => def);
  const isLoading = ctx?.isLoading ?? true;
  const isEditMode = ctx?.isEditMode ?? false;

  useEffect(() => {
    if (isLoading) return;

    // Use relative path (without template prefix) for slug matching
    const slug = relativePath === "/" ? "" : relativePath.replace(/^\//, "").replace(/\/$/, "");

    // Try to find by default slug first
    let pageId = slugToPageId[slug];

    // If not found, check if this slug is a custom slug for any page
    if (!pageId) {
      for (const page of seoPages) {
        const customSlug = getContent(seoKey(page.id, "slug"), page.defaultSlug);
        if (customSlug === slug) {
          pageId = page.id;
          break;
        }
      }
    }

    if (!pageId) {
      // Blog post pages or unknown pages — skip
      return;
    }

    const page = seoPages.find((p) => p.id === pageId);
    if (!page) return;

    // Check if this page's slug has been changed — redirect old slug to new
    if (!isEditMode && page.id !== "home") {
      const configuredSlug = getContent(seoKey(page.id, "slug"), page.defaultSlug);
      if (configuredSlug !== page.defaultSlug && slug === page.defaultSlug) {
        // User is on the old default slug, redirect to the new custom slug
        navigate(`/${configuredSlug}`, { replace: true });
        return;
      }
    }

    // Apply title
    const title = getContent(seoKey(pageId, "title"), page.defaultTitle);
    document.title = title;

    // Apply meta description
    const description = getContent(seoKey(pageId, "description"), page.defaultDescription);
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = description;
  }, [relativePath, isLoading, isEditMode, getContent, navigate]);

  return null;
}